import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadMedia } from "../../lib/api";

const MAX_SECONDS = 60;

/**
 * Inline in-app audio recorder. Uses the browser's MediaRecorder API so
 * the user never has to leave SOSMali. Two visual states:
 *
 *   1. Idle / recording — big record button + live timer.
 *   2. Preview — play / re-record / save controls.
 *
 * Hard 60-second cap (auto-stops). Bails out gracefully with a French
 * toast if the browser denies microphone permission.
 */
export const AudioRecorder = ({ onCancel, onSaved }) => {
  const [status, setStatus] = useState("idle"); // idle | recording | preview | uploading
  const [elapsed, setElapsed] = useState(0);
  const [blobUrl, setBlobUrl] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const tickRef = useRef(0);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const blobRef = useRef(null);

  const cleanup = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch (_e) { /* noop */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = 0;
    }
  };

  useEffect(() => () => {
    cleanup();
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const stopRec = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = 0; }
    try { recorderRef.current?.stop(); } catch (_e) { /* noop */ }
  }, []);

  const startRec = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Enregistrement audio non disponible sur ce navigateur");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        blobRef.current = blob;
        setBlobUrl(URL.createObjectURL(blob));
        setStatus("preview");
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
      mr.start();
      recorderRef.current = mr;
      startTsRef.current = Date.now();
      setElapsed(0);
      setStatus("recording");
      tickRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - startTsRef.current) / 1000);
        setElapsed(s);
        if (s >= MAX_SECONDS) stopRec();
      }, 250);
    } catch (e) {
      toast.error("Microphone refusé. Autorisez l'accès dans les réglages du navigateur.");
    }
  }, [stopRec]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); }
    else { el.play().catch(() => {}); }
  };

  const redo = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl("");
    blobRef.current = null;
    setElapsed(0);
    setStatus("idle");
  };

  const save = async () => {
    if (!blobRef.current) return;
    setStatus("uploading");
    try {
      const file = new File([blobRef.current],
        `audio-${Date.now()}.${(blobRef.current.type || "webm").split("/")[1].split(";")[0]}`,
        { type: blobRef.current.type || "audio/webm" });
      const att = await uploadMedia(file);
      att.duration_s = elapsed;
      onSaved(att);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi de l'audio");
      setStatus("preview");
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div
      className="bg-white dark:bg-zinc-900 border border-mali-red/40 rounded-2xl p-4 shadow-md space-y-3"
      data-testid="audio-recorder"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-mali-navy dark:text-white text-sm">Enregistrer un audio</h3>
        <button
          type="button"
          onClick={() => { cleanup(); onCancel(); }}
          data-testid="audio-recorder-cancel"
          className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground"
        >
          Fermer
        </button>
      </div>

      <div className="flex items-center justify-center gap-4">
        {status === "idle" && (
          <button
            type="button"
            onClick={startRec}
            data-testid="audio-recorder-start"
            className="w-16 h-16 rounded-full bg-mali-red text-white shadow-lg shadow-mali-red/40 flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Démarrer l'enregistrement"
          >
            <Mic className="w-6 h-6" strokeWidth={2.5} />
          </button>
        )}
        {status === "recording" && (
          <button
            type="button"
            onClick={stopRec}
            data-testid="audio-recorder-stop"
            className="w-16 h-16 rounded-full bg-mali-red text-white shadow-lg shadow-mali-red/40 flex items-center justify-center animate-pulse"
            aria-label="Arrêter l'enregistrement"
          >
            <Square className="w-5 h-5" strokeWidth={2.5} fill="currentColor" />
          </button>
        )}
        {(status === "preview" || status === "uploading") && (
          <button
            type="button"
            onClick={togglePlay}
            data-testid="audio-recorder-play"
            disabled={status === "uploading"}
            className="w-16 h-16 rounded-full bg-mali-navy text-white shadow-md flex items-center justify-center disabled:opacity-60"
            aria-label={playing ? "Pause" : "Lire"}
          >
            {playing ? <Pause className="w-6 h-6" strokeWidth={2.5} /> : <Play className="w-6 h-6" strokeWidth={2.5} />}
          </button>
        )}

        <div className="text-center">
          <div className="font-mono text-3xl font-bold tabular-nums text-mali-navy dark:text-white" data-testid="audio-recorder-timer">
            {mm}:{ss}
          </div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] mt-0.5">
            {status === "recording" ? "Enregistrement…"
              : status === "preview" ? "Prêt à envoyer"
              : status === "uploading" ? "Envoi…"
              : `Maximum ${MAX_SECONDS} s`}
          </div>
        </div>
      </div>

      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="hidden"
          aria-label="Aperçu audio enregistré"
        >
          <track kind="captions" />
        </audio>
      )}

      {(status === "preview" || status === "uploading") && (
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={redo}
            disabled={status === "uploading"}
            data-testid="audio-recorder-redo"
            className="flex-1 bg-muted text-foreground font-bold text-xs py-2.5 rounded-xl flex items-center justify-center gap-1.5 hover:bg-muted/70 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Refaire
          </button>
          <button
            type="button"
            onClick={save}
            disabled={status === "uploading"}
            data-testid="audio-recorder-save"
            className="flex-[2] bg-mali-red text-white font-bold text-xs py-2.5 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-70"
          >
            {status === "uploading" ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi…</>
            ) : (
              <><Check className="w-3.5 h-3.5" /> Joindre à l&apos;alerte</>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
